import { Component, OnInit, ViewContainerRef, computed, effect, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ToastrService } from "ngx-toastr";
import { ClippingStudioManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-manager.service";
import {
  ClippingStudioTextEvent,
  TextAlignment,
} from "app/js/src/domain/clipping-studio/clipping-studio-project";
import {
  ClippingStudioTextTemplate,
  ClippingStudioTextTemplateWritePayload,
} from "app/js/src/domain/clipping-studio/clipping-studio-text-template";
import { ITextTemplatesProvider } from "app/js/src/domain/clipping-studio/i-text-templates-provider";
import {
  BOLD_FONT_WEIGHT,
  REGULAR_FONT_WEIGHT,
} from "app/js/src/domain/clipping-studio/clipping-studio-text-layout";
import {
  createInitialClippingStudioState,
  ClippingStudioState,
} from "app/js/src/domain/clipping-studio/clipping-studio-state";
import { IEvrideoDialogService } from "app/js/src/domain/common/i-evrideo-dialog-service";

const TEXT_ALIGNMENTS: TextAlignment[] = ["left", "center", "right"];

function toTextAlignment(value: string | undefined): TextAlignment | undefined {
  if (value && TEXT_ALIGNMENTS.includes(value as TextAlignment)) {
    return value as TextAlignment;
  }
  return undefined;
}

@Component({
  selector: "evrideo-clipping-studio-text-panel",
  templateUrl: "./clipping-studio-text-panel.component.html",
  styleUrls: ["./clipping-studio-text-panel.component.scss"],
})
export class ClippingStudioTextPanelComponent implements OnInit {
  private readonly initialState = createInitialClippingStudioState();

  readonly state = toSignal(this.manager.state$, {
    initialValue: this.initialState,
  });

  readonly templates = signal<ClippingStudioTextTemplate[]>([]);
  readonly textTemplatesLoading = signal(false);
  readonly textTemplatesError = signal<string | null>(null);
  /** Session template id from catalog card; null for Heading/Body/preview re-select. */
  readonly activeTemplateId = signal<number | null>(null);
  readonly templateActionBusy = signal(false);

  readonly selectedText = computed(() => {
    const s = this.state();
    const id = s?.selectedTextId;
    if (!id) return null;
    const effectiveText = this.manager.getEffectiveText(
      s as ClippingStudioState,
      this.manager.currentPreviewDestinationId,
    );
    return effectiveText.find((t) => t.id === id) ?? null;
  });

  readonly isEditMode = computed(() => this.state().mode === "EDIT");

  private loadGeneration = 0;
  private openedTextId: string | null = null;
  private openedTextSnapshot: ClippingStudioTextEvent | null = null;

  constructor(
    readonly manager: ClippingStudioManagerService,
    private readonly textTemplatesProvider: ITextTemplatesProvider,
    private readonly toastr: ToastrService,
    private readonly dialogService: IEvrideoDialogService,
    private readonly viewContainerRef: ViewContainerRef,
  ) {
    // Clear template session on any deselection (Save/X, Escape, keyboard Delete, etc.).
    effect(
      () => {
        const selectedId = this.state()?.selectedTextId ?? null;
        if (selectedId == null) {
          this.activeTemplateId.set(null);
          this.openedTextId = null;
          this.openedTextSnapshot = null;
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadTextTemplates();
  }

  private loadTextTemplates(): void {
    const generation = ++this.loadGeneration;
    this.textTemplatesLoading.set(true);
    this.textTemplatesError.set(null);
    this.textTemplatesProvider
      .getTextTemplates()
      .then((list) => {
        if (generation !== this.loadGeneration) return;
        this.templates.set(list);
      })
      .catch((err) => {
        if (generation !== this.loadGeneration) return;
        this.textTemplatesError.set(
          err?.message ?? "Failed to load text templates",
        );
        // Keep previous catalog on refresh failure (do not wipe to empty).
      })
      .finally(() => {
        if (generation !== this.loadGeneration) return;
        this.textTemplatesLoading.set(false);
      });
  }

  addHeading(): void {
    if (this.state().mode !== "EDIT") return;
    this.activeTemplateId.set(null);
    this.manager.addText({
      text: "ADD HEADING",
      fontSize: 48,
      fontWeight: BOLD_FONT_WEIGHT,
      fontColor: "#ffffff",
    });
  }

  addBodyText(): void {
    if (this.state().mode !== "EDIT") return;
    this.activeTemplateId.set(null);
    this.manager.addText({
      text: "Add body text",
      fontSize: 18,
      fontWeight: REGULAR_FONT_WEIGHT,
      fontColor: "#ffffff",
    });
  }

  /**
   * Catalog card body: apply template to the timeline without opening the styling editor.
   */
  onAddTemplate(tmpl: ClippingStudioTextTemplate): void {
    if (!this.isEditMode()) return;
    this.applyTemplateToTimeline(tmpl, { openEditor: false });
  }

  /**
   * Catalog card Edit action: apply template and open the styling editor (previous card-click behavior).
   */
  onEditTemplate(tmpl: ClippingStudioTextTemplate): void {
    if (!this.isEditMode()) return;
    this.applyTemplateToTimeline(tmpl, { openEditor: true });
  }

  onDeleteTemplateFromCard(tmpl: ClippingStudioTextTemplate): void {
    void this.onDeleteTemplate(tmpl.id);
  }

  private applyTemplateToTimeline(
    tmpl: ClippingStudioTextTemplate,
    options: { openEditor: boolean },
  ): void {
    this.activeTemplateId.set(null);
    const beforeId = this.manager.state.selectedTextId;
    this.manager.addText({
      text: tmpl.text,
      fontSize: tmpl.fontSize,
      fontWeight: tmpl.fontWeight,
      fontColor: tmpl.fontColor,
      fontStyle: tmpl.fontStyle,
      fontFamily: tmpl.fontFamily,
      alignment: toTextAlignment(tmpl.alignment),
      bold: tmpl.bold,
      italic: tmpl.italic,
      backgroundColor: tmpl.backgroundColor,
      backgroundPadding: tmpl.backgroundPadding,
      backgroundBorderRadius: tmpl.backgroundBorderRadius,
      dropShadow: tmpl.dropShadow,
      shadowColor: tmpl.shadowColor,
      shadowBlur: tmpl.shadowBlur,
      shadowOffsetX: tmpl.shadowOffsetX,
      shadowOffsetY: tmpl.shadowOffsetY,
    });

    if (!options.openEditor) {
      this.manager.selectText(null);
      this.activeTemplateId.set(null);
      this.openedTextId = null;
      this.openedTextSnapshot = null;
      return;
    }

    const afterId = this.manager.state.selectedTextId;
    if (afterId && afterId !== beforeId) {
      this.activeTemplateId.set(tmpl.id);
    }
  }

  onUpdateText(changes: Partial<ClippingStudioTextEvent>): void {
    const text = this.selectedText();
    if (text) {
      if (this.openedTextId !== text.id) {
        this.openedTextId = text.id;
        this.openedTextSnapshot = { ...text };
      }
      this.manager.updateText(text.id, changes);
    }
  }

  onPreviewText(changes: Partial<ClippingStudioTextEvent>): void {
    const text = this.selectedText();
    if (!text) return;
    if (this.openedTextId !== text.id) {
      this.openedTextId = text.id;
      this.openedTextSnapshot = { ...text };
    }
    this.manager.updateText(text.id, changes, {
      skipUndo: true,
      skipPersist: true,
    });
  }

  onCloseEditor(e: { saved: boolean }): void {
    const text = this.selectedText();
    if (text) {
      if (this.openedTextId !== text.id) {
        this.openedTextId = text.id;
        this.openedTextSnapshot = { ...text };
      }
      if (
        e.saved === false &&
        this.openedTextSnapshot &&
        this.openedTextSnapshot.id === text.id
      ) {
        const revert: Partial<ClippingStudioTextEvent> = {
          ...this.openedTextSnapshot,
        };
        delete (revert as { id?: string }).id;
        this.manager.updateText(text.id, revert, {
          skipUndo: true,
          skipPersist: true,
        });
      }
    }
    this.manager.selectText(null);
    this.activeTemplateId.set(null);
    this.openedTextId = null;
    this.openedTextSnapshot = null;
  }

  async onSaveAsTemplate(
    payload: ClippingStudioTextTemplateWritePayload,
  ): Promise<void> {
    if (this.templateActionBusy()) return;
    this.templateActionBusy.set(true);
    try {
      await this.textTemplatesProvider.createTextTemplate(payload);
      this.toastr.success("Template saved.");
      this.loadTextTemplates();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not save template.";
      this.toastr.error(message);
    } finally {
      this.templateActionBusy.set(false);
    }
  }

  async onUpdateTemplate(e: {
    id: number;
    payload: ClippingStudioTextTemplateWritePayload;
  }): Promise<void> {
    if (this.templateActionBusy()) return;
    this.templateActionBusy.set(true);
    try {
      await this.textTemplatesProvider.updateTextTemplate(e.id, e.payload);
      this.toastr.success("Template updated.");
      this.loadTextTemplates();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not update template.";
      this.toastr.error(message);
    } finally {
      this.templateActionBusy.set(false);
    }
  }

  async onDeleteTemplate(id: number): Promise<void> {
    if (this.templateActionBusy()) return;

    const confirmed = await this.dialogService.openDialog(this.viewContainerRef, {
      type: "warning",
      title: "Delete template",
      content: "Are you sure you want to delete this text template? This cannot be undone.",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      timeout: -1,
    });
    if (!confirmed) return;

    this.templateActionBusy.set(true);
    try {
      await this.textTemplatesProvider.deleteTextTemplate(id);
      this.toastr.success("Template deleted.");
      this.activeTemplateId.set(null);
      this.manager.selectText(null);
      this.openedTextId = null;
      this.openedTextSnapshot = null;
      this.loadTextTemplates();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not delete template.";
      this.toastr.error(message);
    } finally {
      this.templateActionBusy.set(false);
    }
  }
}
